/**
 * Shared constants + tiny predicates for the dorm Style-It decor planners.
 *
 * One tunables table for the whole `plan/` directory so a dimension or margin
 * is never defined twice. Everything here is pure data (plus two one-line
 * predicates that qualify an object against it).
 */

const IN_TO_M = 0.0254;

// A student-marked built-in closet. Immovable, and the optimizer reserves an
// 18" access strip in front of it that decor must respect too — otherwise a
// correct layout gets a plant planted in the closet doorway on the next
// "Style It" tap. Mirrors FIXED_OBSTACLE_CLEARANCE_M in the dorm optimizer.
const BUILTIN_KEEPOUT_TYPE = 'dorm-fixed-keepout';
const BUILTIN_CLEARANCE_M = 18 * IN_TO_M;

function isBuiltInKeepout(obj) {
  return obj?.type === BUILTIN_KEEPOUT_TYPE || obj?.isFixed === true;
}

/**
 * A wardrobe stood mid-room as a privacy screen. Not a keep-out — it earns no
 * clearance strip and decor may sit right beside it — but it IS a solid body
 * standing where the floor would otherwise be empty, which is exactly where
 * floor decor assumes it can spread.
 */
function isDividerBody(obj) {
  return obj?.isDivider === true;
}

/** Extra distance decor must keep from `obj` beyond its own footprint. */
function requiredClearance(obj) {
  return isBuiltInKeepout(obj) ? BUILTIN_CLEARANCE_M : 0;
}

// Contract type-key default dimensions (meters). Mirror the web-app procedural
// gallery registry (defaultDimensions.js) so decor matches its renderer's axes.
const RUG_RECT_DIMS = {
  x: 60 * IN_TO_M,
  y: 0.4 * IN_TO_M,
  z: 48 * IN_TO_M,
};
const RUG_CIRCLE_DIMS = {
  x: 48 * IN_TO_M,
  y: 0.4 * IN_TO_M,
  z: 48 * IN_TO_M,
};
const FAIRY_LIGHTS_DIMS = {
  x: 96 * IN_TO_M, // span along the wall
  y: 8 * IN_TO_M, // vertical droop
  z: 2 * IN_TO_M, // depth off the wall
};
// Photo string reuses the fairy-lights catenary dims (span/droop/depth).
const PHOTO_STRING_DIMS = {
  x: 96 * IN_TO_M,
  y: 8 * IN_TO_M,
  z: 2 * IN_TO_M,
};

// Leave a margin between the rug edge and the walls so it never spills out.
const RUG_WALL_INSET_M = 0.3; // 30 cm
// Push fairy-lights just off the wall surface so they don't z-fight the plane.
const FAIRY_WALL_OFFSET_M = 0.05; // 5 cm
// Hang fairy-lights this far below the ceiling.
const FAIRY_CEILING_DROP_M = 0.15; // 15 cm
// Trim each end of a wall's light run so adjacent walls' runs meet at the corner.
const FAIRY_CORNER_MARGIN_M = 0.2; // 20 cm total (10 cm per end)
// Photo string hangs at eye/above-desk level (center height, floor-relative)
// on ONE feature wall, not near the ceiling — so the polaroids are readable.
const PHOTO_STRING_CENTER_Y_M = 1.6; // ~63 in, above-desk / eye level
// Span most of the feature wall (leave a margin at each end).
const PHOTO_STRING_END_MARGIN_M = 0.4; // 40 cm total (20 cm per end)

// ─── Photo wall (grid / collage of framed art) ──────────────────────
// Eye level for hung frames = floorY + this height. Frames sit centered here.
const PHOTO_WALL_EYE_HEIGHT_M = 1.5; // ~59 in
// A furniture object counts as "touching the wall" when its nearest footprint
// corner is within this distance of the wall base segment.
const WALL_TOUCH_DISTANCE_M = 0.6; // 60 cm — tall furniture (esp. lofts) sits inset from the wall over under-bed space; catch it
// Fixed frame footprint (~10 in wide × 14 in tall) — abstract framed art board.
const FRAME_WIDTH_M = 10 * IN_TO_M;
const FRAME_HEIGHT_M = 14 * IN_TO_M;
const FRAME_DEPTH_M = 0.5 * IN_TO_M;
// Gap between frames (both axes) when tiled in the grid.
const FRAME_GAP_M = 3 * IN_TO_M;
// Leave a small margin at each end of the chosen sub-span so frames don't kiss
// the sub-span edge (and, via the side bias, the wall obstacle beyond it).
const SUBSPAN_EDGE_MARGIN_M = 0.1; // 10 cm
// Push frames just off the wall surface (past the thick wall box) so they read
// as hung art and don't z-fight the wall plane.
const FRAME_WALL_OFFSET_M = 0.03; // 3 cm

// Hanging planter dims mirror the decor-plants catalog (10w × 14h × 10d in),
// the same source the GLB was measured from at upload.
const HANGING_PLANTER_DIMS = {
  x: 10 * IN_TO_M,
  y: 14 * IN_TO_M,
  z: 10 * IN_TO_M,
};
// Leave this gap between the top of the planter and the ceiling (it hangs).
const HANGING_PLANTER_CEILING_GAP_M = 0.15; // 15 cm
// Nudge the planter in from its corner toward the room interior so the pot and
// its trailing leaves clear the two walls of the corner.
const HANGING_PLANTER_CORNER_INSET_M = 0.4; // 40 cm along each axis
// Minimum corner-to-corner spacing when fanning multiple hanging planters over
// distinct corners so two planters never share (or crowd) the same corner.
const HANGING_PLANTER_MIN_SPACING_M = 1.0; // 1 m between chosen corners

// ─── Plants (surface / windowsill / floor-corner) ───────────────────
// Small desk-plant footprint (mirrors the decor-plants catalog: ~5w × 8h × 5d).
const DESK_PLANT_DIMS = {
  x: 5 * IN_TO_M,
  y: 8 * IN_TO_M,
  z: 5 * IN_TO_M,
};
// Furniture types with a flat usable top a surface plant can sit on.
const SURFACE_PLANT_HOST_TYPES = new Set([
  'dorm-desk',
  'dorm-dresser',
  'dorm-bookshelf',
  'dorm-bookshelf-wide',
  'dorm-stackable-drawers',
  'dorm-minifridge-compact',
  'dorm-minifridge-midsize',
  // The top of a wardrobe is dead space students reliably decorate — it is
  // the one flat surface at eye level or above. Note this set is shared with
  // surface PLANTS, so a trailing plant can land up there too, which is also
  // how people actually use it.
  'dorm-wardrobe',
]);
// A windowsill plant sits in the window REVEAL — the ledge left where the
// opening is carved out of a wall with thickness. Its inward offset is
// therefore derived per window from that wall's thickness and the pot's own
// depth (see planWindowsillPlants), not fixed: a constant 10 cm pushed the pot
// clear of a 15 cm wall entirely and it floated in mid-air in front of the
// glass. Kept only as the fallback when a window's host wall can't be found.
const WINDOWSILL_INWARD_OFFSET_M = 0.01; // 1 cm
// Floor-corner plant footprint (tall floor planter: ~10w × 30h × 10d).
const FLOOR_PLANT_DIMS = {
  x: 10 * IN_TO_M,
  y: 30 * IN_TO_M,
  z: 10 * IN_TO_M,
};
// Step a floor-corner plant this far in from its corner toward the centroid so
// the planter clears both corner walls.
const FLOOR_PLANT_CORNER_INSET_M = 0.35; // 35 cm along each axis
// A floor corner is "clear" only when every furniture footprint is at least this
// far from the inset plant center (plant radius + breathing margin).
const FLOOR_PLANT_CLEAR_MARGIN_M = 0.25; // 25 cm beyond the plant half-footprint
// Hang a wall vine just below the ceiling like the fairy-lights string.
const VINE_DIMS = {
  x: 96 * IN_TO_M, // span along the wall
  y: 8 * IN_TO_M, // vertical droop
  z: 2 * IN_TO_M, // depth off the wall
};

// ─── Knickknacks (small personal decor seated on furniture tops) ────
// Per-type catalog footprints (meters). This is the ONLY home for them: the
// web procedural gallery DERIVES its entries from here (`dimsFrom` in
// defaultDimensions.js), which throws at import on a missing key — a stronger
// guarantee than the equality test an older comment here promised and that
// never actually existed.
const KNICKKNACK_DIMS = {
  'dorm-rubiks-cube': { x: 2.2 * IN_TO_M, y: 2.2 * IN_TO_M, z: 2.2 * IN_TO_M },
  'dorm-plush': { x: 7 * IN_TO_M, y: 9 * IN_TO_M, z: 5 * IN_TO_M },
  'dorm-book-stack': { x: 8 * IN_TO_M, y: 4.5 * IN_TO_M, z: 6 * IN_TO_M },
  'dorm-mug': { x: 4.5 * IN_TO_M, y: 4 * IN_TO_M, z: 3.5 * IN_TO_M },
  'dorm-snack-stack': { x: 6 * IN_TO_M, y: 7 * IN_TO_M, z: 4 * IN_TO_M },
  'dorm-candle': { x: 3 * IN_TO_M, y: 3.5 * IN_TO_M, z: 3 * IN_TO_M },
  // Theme knickknacks. `dorm-plush-dog` / `dorm-plush-cat` are SIBLINGS of
  // `dorm-plush`, not replacements — the bear still ships and every persisted
  // one must stay strippable.
  'dorm-shell-cluster': { x: 6 * IN_TO_M, y: 3 * IN_TO_M, z: 5 * IN_TO_M },
  'dorm-starfish': { x: 5 * IN_TO_M, y: 1.2 * IN_TO_M, z: 5 * IN_TO_M },
  'dorm-boombox': { x: 12 * IN_TO_M, y: 6.5 * IN_TO_M, z: 4 * IN_TO_M },
  'dorm-cassette-stack': { x: 4.5 * IN_TO_M, y: 2 * IN_TO_M, z: 3 * IN_TO_M },
  'dorm-camp-lantern': { x: 5 * IN_TO_M, y: 9 * IN_TO_M, z: 5 * IN_TO_M },
  'dorm-fish-bowl': { x: 8 * IN_TO_M, y: 8 * IN_TO_M, z: 8 * IN_TO_M },
  'dorm-terrarium': { x: 5.5 * IN_TO_M, y: 9 * IN_TO_M, z: 5.5 * IN_TO_M },
  'dorm-dried-bundle': { x: 6 * IN_TO_M, y: 15 * IN_TO_M, z: 6 * IN_TO_M },
  'dorm-plush-dog': { x: 8 * IN_TO_M, y: 9 * IN_TO_M, z: 6 * IN_TO_M },
  'dorm-plush-cat': { x: 7 * IN_TO_M, y: 8.5 * IN_TO_M, z: 6 * IN_TO_M },
};
// Spacing between adjacent knickknacks in a packed row.
const KNICKKNACK_GAP_M = 2 * IN_TO_M;
// Keep-back from the host top's side edges (nothing teeters on a corner).
const KNICKKNACK_EDGE_MARGIN_M = 1.5 * IN_TO_M;

// ─── Wall art (mirror / tapestry / poster / shelf / board) ──────────
// Fixed catalog footprints per wall-art type (meters), sized to read as the
// item at a glance. Depth is small (they hang flush to the wall). A single
// centered piece per entry; multiple `count` fan along the wall run.
const WALL_ART_DIMS = {
  'dorm-mirror': { x: 20 * IN_TO_M, y: 40 * IN_TO_M, z: 1 * IN_TO_M },
  'dorm-tapestry': { x: 40 * IN_TO_M, y: 56 * IN_TO_M, z: 0.5 * IN_TO_M },
  'dorm-poster': { x: 18 * IN_TO_M, y: 24 * IN_TO_M, z: 0.5 * IN_TO_M },
  'dorm-corkboard': { x: 24 * IN_TO_M, y: 18 * IN_TO_M, z: 1 * IN_TO_M },
  'dorm-whiteboard': { x: 24 * IN_TO_M, y: 18 * IN_TO_M, z: 1 * IN_TO_M },
  'dorm-floating-shelf': { x: 30 * IN_TO_M, y: 2 * IN_TO_M, z: 6 * IN_TO_M },
  // Theme wall art. The oar and the pennant banner are the two widest pieces
  // in the catalogue — `packRowAcross` BREAKS on the first misfit, so on a
  // busy wall they are the first things silently dropped.
  'dorm-wall-oar': { x: 54 * IN_TO_M, y: 7 * IN_TO_M, z: 1.5 * IN_TO_M },
  'dorm-rattan-fan': { x: 28 * IN_TO_M, y: 28 * IN_TO_M, z: 1.5 * IN_TO_M },
  'dorm-vinyl-wall': { x: 42 * IN_TO_M, y: 28 * IN_TO_M, z: 1 * IN_TO_M },
  'dorm-neon-sign': { x: 24 * IN_TO_M, y: 14 * IN_TO_M, z: 2 * IN_TO_M },
  'dorm-pennant-banner': { x: 60 * IN_TO_M, y: 12 * IN_TO_M, z: 1 * IN_TO_M },
};
// Center height of a hung wall-art piece (floor-relative), at eye level so it
// reads as intentional gallery placement (same band as the photo wall).
const WALL_ART_EYE_HEIGHT_M = 1.55; // ~61 in
// Per-type center-height overrides: a shelf or pinboard hangs at desk-reach,
// not gallery eye level. Absent → WALL_ART_EYE_HEIGHT_M.
const WALL_ART_CENTER_HEIGHT_M = {
  'dorm-floating-shelf': 1.35,
  'dorm-corkboard': 1.4,
  'dorm-whiteboard': 1.4,
  // A banner is strung high, near the wall-ceiling line; an oar hangs above
  // eye level because it is read lengthwise; a neon sign sits a little low so
  // its glow lands on the desk.
  'dorm-pennant-banner': 1.95,
  'dorm-wall-oar': 1.9,
  'dorm-neon-sign': 1.7,
};
// ─── Floor decor (free-standing Style-It pieces seated on the floor) ───
// Distinct from FLOOR_PLANT_DIMS: those are one planner's fixed catalogue,
// this is the registry `planFloorDecor` fans a bundle spec over.
const FLOOR_DECOR_DIMS = {
  'dorm-pet-bed': { x: 24 * IN_TO_M, y: 8 * IN_TO_M, z: 24 * IN_TO_M },
};
// Corner inset for floor decor. NOT the plant's 0.35: the bed's half-footprint
// is 0.305, which would leave it 4.5 cm off both walls and read as jammed in.
const FLOOR_DECOR_CORNER_INSET_M = 0.45;
// Clearance margin. Note `isFloorPointClear` takes
// `max(clearDist, requiredClearance(obj))`, so this is inert against built-in
// keepouts — it is an aesthetic knob, not a pass-rate one.
const FLOOR_DECOR_CLEAR_MARGIN_M = 0.1;

// Spacing between fanned pieces of one wall-art entry (min, scales with item).
const WALL_ART_MIN_SPACING_M = 0.15;
// Gap between the wall face and the piece's BACK face, so it doesn't z-fight
// the plane. The optimizer's wall-mounted placer uses the same 1 cm. Measured
// to the back, not the centre: a centre 3 cm off the wall sank a 6"-deep
// floating shelf 1.8" into it (#3110).
const WALL_ART_WALL_OFFSET_M = 0.01; // 1 cm
// Fraction of the wall run over which multiple pieces of one entry are spread.
const WALL_ART_SPREAD_FRACTION = 0.6;

module.exports = {
  IN_TO_M,
  BUILTIN_KEEPOUT_TYPE,
  BUILTIN_CLEARANCE_M,
  isBuiltInKeepout,
  isDividerBody,
  requiredClearance,
  RUG_RECT_DIMS,
  RUG_CIRCLE_DIMS,
  FAIRY_LIGHTS_DIMS,
  PHOTO_STRING_DIMS,
  RUG_WALL_INSET_M,
  FAIRY_WALL_OFFSET_M,
  FAIRY_CEILING_DROP_M,
  FAIRY_CORNER_MARGIN_M,
  PHOTO_STRING_CENTER_Y_M,
  PHOTO_STRING_END_MARGIN_M,
  PHOTO_WALL_EYE_HEIGHT_M,
  WALL_TOUCH_DISTANCE_M,
  FRAME_WIDTH_M,
  FRAME_HEIGHT_M,
  FRAME_DEPTH_M,
  FRAME_GAP_M,
  SUBSPAN_EDGE_MARGIN_M,
  FRAME_WALL_OFFSET_M,
  HANGING_PLANTER_DIMS,
  HANGING_PLANTER_CEILING_GAP_M,
  HANGING_PLANTER_CORNER_INSET_M,
  HANGING_PLANTER_MIN_SPACING_M,
  DESK_PLANT_DIMS,
  SURFACE_PLANT_HOST_TYPES,
  WINDOWSILL_INWARD_OFFSET_M,
  FLOOR_PLANT_DIMS,
  FLOOR_PLANT_CORNER_INSET_M,
  FLOOR_PLANT_CLEAR_MARGIN_M,
  VINE_DIMS,
  KNICKKNACK_DIMS,
  KNICKKNACK_GAP_M,
  KNICKKNACK_EDGE_MARGIN_M,
  WALL_ART_DIMS,
  FLOOR_DECOR_DIMS,
  FLOOR_DECOR_CORNER_INSET_M,
  FLOOR_DECOR_CLEAR_MARGIN_M,
  WALL_ART_EYE_HEIGHT_M,
  WALL_ART_CENTER_HEIGHT_M,
  WALL_ART_WALL_OFFSET_M,
  WALL_ART_SPREAD_FRACTION,
  WALL_ART_MIN_SPACING_M,
};
